import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  Auth,
  AuthCredential,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  linkWithCredential,
  OAuthProvider,
  reload,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  User,
  UserCredential,
} from '@angular/fire/auth';
import { Location } from '@angular/common';
import { NotificationService } from '../services/notification.service';
import { AuthProvider } from './auth-providers';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { lastValueFrom } from 'rxjs';

export interface PendingAuth {
  credential: AuthCredential;
  email: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  protected router = inject(Router);
  private afa = inject(Auth);
  private notificationService = inject(NotificationService);
  private location = inject(Location);
  private functions = inject(AngularFireFunctions);

  private pendingAuth: PendingAuth;
  private lastRedirectUrl: string;

  public async signinWithProvider(
    provider: AuthProvider,
  ): Promise<UserCredential> {
    switch (provider) {
      case AuthProvider.GOOGLE:
        return this.signinGoogle();
      case AuthProvider.MICROSOFT:
        return this.signinMicrosoft();
      default:
        throw new Error(
          `Signin provider '${provider}' isn't implemented. Please add implementation in AuthService`,
        );
    }
  }

  public setPendingAuth(pendingAuth: PendingAuth) {
    this.pendingAuth = pendingAuth;
  }

  public async signinGoogle(): Promise<UserCredential> {
    const result = await signInWithPopup(this.afa, new GoogleAuthProvider());
    await this.afterSignIn(result);
    return result;
  }

  private async signinMicrosoft(): Promise<UserCredential> {
    const autoVerifyUserEmailFunction = this.functions.httpsCallable(
      'autoVerifyUserEmail',
    );
    const provider = new OAuthProvider('microsoft.com');
    provider.setCustomParameters({ prompt: 'select_account' });
    const result = await signInWithPopup(this.afa, provider);
    if (!result.user.emailVerified) {
      await lastValueFrom(autoVerifyUserEmailFunction({}));
      await reload(result.user);
      await result.user.getIdToken(true);
    }
    await this.afterSignIn(result);
    return result;
  }

  public async signinWithEmail(
    email: string,
    password: string,
  ): Promise<UserCredential> {
    const result = await signInWithEmailAndPassword(this.afa, email, password);
    await this.afterSignIn(result);
    return result;
  }

  public async registerWithEmail(
    email: string,
    password: string,
  ): Promise<UserCredential> {
    const result = await createUserWithEmailAndPassword(
      this.afa,
      email,
      password,
    );
    await this.sendEmailVerification(result.user);
    await this.afterSignIn(result);
    return result;
  }

  public async sendEmailVerification(user: User): Promise<void> {
    await sendEmailVerification(user, {
      url: location.origin + '/auth/email-verified',
    });
  }

  public async sendPasswordResetEmail(email: string) {
    return await sendPasswordResetEmail(this.afa, email);
  }

  public async afterSignIn(result: UserCredential) {
    if (this.pendingAuth) {
      if (this.pendingAuth?.email === result.user.email) {
        await linkWithCredential(result.user, this.pendingAuth.credential);
        this.notificationService.showMessage(
          'New auth method successfully connected',
        );
      } else {
        this.notificationService.showMessage(
          "Pending auth email isn't the same with active one. Skip connecting auth providers",
        );
      }
      this.pendingAuth = null;
    }
    await this.router.navigateByUrl(this.getRedirectUrl());
  }

  public setRedirectUrl(url?: string) {
    localStorage.setItem('redirectUrl', url || this.location.path());
  }

  public getRedirectUrl(): string {
    const storedRedirectUrl = localStorage.getItem('redirectUrl');
    if (storedRedirectUrl && this.lastRedirectUrl !== storedRedirectUrl) {
      this.lastRedirectUrl = storedRedirectUrl; // additional param to avoid redirects loop
      localStorage.removeItem('redirectUrl');
      return storedRedirectUrl;
    }
    return '/';
  }
}
